import React, { useState, useEffect } from 'react';
import { BookOpen, Send, ClipboardCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuthStore } from "../store/authStore";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Optional for table formatting

const AssignmentForm = () => {
  const [assignmentText, setAssignmentText] = useState('');
  const [grade, setGrade] = useState('');
  const [genres, setGenres] = useState('');
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false); // Disable submit initially


  const { user, updateCredits, credits, consumeCredits } = useAuthStore();

  const gradeOptions = [
    { value: 'primary', label: 'Student - Primary (Ages 5 - 11)' },
    { value: 'lower_secondary', label: 'Student - Lower Secondary (Ages 12 - 15)' },
    { value: 'upper_secondary', label: 'Student - Upper Secondary (Ages 16 - 19)' },
    { value: 'graduate', label: 'Student - Graduate / PG Researcher' },
    { value: 'author', label: 'Author' },
    { value: 'teacher', label: 'Language Teacher / Coach' },
  ];

  const genresOptions = {
    'Fiction Genres': [
      'Literary Fiction', 'Mystery/Crime', 'Thriller/Suspense', 'Horror', 'Fantasy',
      'Science Fiction (Sci-Fi)', 'Romance', 'Historical Fiction', 'Contemporary Fiction',
      'Adventure', 'Magical Realism', 'Comedy/Humor'
    ],
    'Nonfiction Genres': [
      'Biography/Autobiography', 'Memoir', 'Self-Help', 'Travel Writing', 'History',
      'Science Writing', 'Journalism/Reportage', 'Personal Essays', 'College Essays',
      'Profile Writing', 'How-To/Instructional', 'Philosophy', 'Spiritual/Religious Writing',
      'Academic Writing', 'Political Writing', 'Food Writing', 'Persuasive Writing', 'Debate/Argumentative Writing'
    ],
    'Drama and Scriptwriting Genres': [
      'Stage Plays', 'Screenwriting (Film, TV)'
    ],
    'Hybrid/Experimental Genres': [
      'Flash Fiction', 'Novella', 'Prose Poetry', 'Cli-Fi (Climate Fiction)'
    ],
    'Professional/Functional Writing Genres': [
      'Technical Writing (Manuals, Reports)', 'Business Writing', 'Grant Writing',
      'Speechwriting', 'Content Writing/Blogging', 'White Papers', 'Reports and Proposals',
      'Research Papers', 'Thesis'
    ],
    'Interactive/Modern Genres': [
      'Game Writing', 'Social Media Stories/Posts'
    ],
    'Poetry Genres': [
      'Lyric Poetry', 'Narrative Poetry', 'Epic Poetry', 'Free Verse', 'Haiku',
      'Sonnets', 'Elegy', 'Ode', 'Limerick'
    ]
  };

  const evaluationOptions = {
    'Content': [
      'Genre Adherence',
      'Coherence and Cohesion'
    ],
    'Organization': [
      'Paragraph order and logic',
      'Sentence Structure'
    ],
    'Language': [
      'Vocabulary Richness',
      'Grammatical Range and Accuracy'
    ],
    'Thought Process': [
      'Creativity and Innovation',
      'Critical Thinking',
      'Mindset'
    ],
    'Communication': [
      'Appropriateness to Genre',
      'Formality and Tone as per Genre',
      'Engaging Content',
      'Conciseness'
    ],
    'Emotion': ['Emotional Awareness', 'Empathy']
  };

  const maxWordCount = 1000;
  const minWordCount = 50;
  const wordCount = assignmentText.split(/\s+/).filter((word) => word).length;


  const handleTextChange = (e) => {
    const text = e.target.value;
    const words = text.split(/\s+/);

    // Check word count
    if (words.length > maxWordCount) {
      const truncatedText = words.slice(0, maxWordCount).join(" ");
      setAssignmentText(truncatedText);
      setWarningMessage("Your text is truncated. Maximum word limit is 1000 words.");
    } else if (words.length < minWordCount) {
      setAssignmentText(text);
      setWarningMessage(`Your text should have at least ${minWordCount} words.`);
      setIsSubmitDisabled(true); // Disable submit button if less than 50 words
    } else {
      setAssignmentText(text);
      setWarningMessage(""); // Clear warning if text is within limit
      setIsSubmitDisabled(false); // Enable submit button if word count is valid
    }
  };

  const handleEvaluationCriteriaChange = (criterion) => {
    setEvaluationCriteria(prev => 
      prev.includes(criterion)
        ? prev.filter(c => c !== criterion)
        : [...prev, criterion]
    );
  };

  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!assignmentText.trim() || !grade || !genres || evaluationCriteria.length === 0) {
      setError('Please provide all parameters including at least one evaluation criterion');
      return;
    }

    setIsLoading(true);
    setError(null);
    setReport(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          story: assignmentText,
          genres: genres,
          grade: grade,
          evaluationCriteria: evaluationCriteria,
          email: user.email
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.message) {
          setError(errorData.message); // Display the error from the backend (e.g., insufficient credits)
        } else {
          setError('Failed to submit assignment. Please try again.');
        }
        return;
      }

      const data = await response.json();
      const newCredits = credits - 5;
      updateCredits(newCredits);

      // Format the suggestions as an array of individual points
      const formattedReport = {
        ...data,
        detailedAnalysis: Object.entries(data.detailedAnalysis).reduce((acc, [category, details]) => {
          // Replace underscores with spaces in the category name
          const formattedCategory = category.replace(/_/g, ' ');

          acc[formattedCategory] = {
            ...details,
            suggestions: Array.isArray(details.suggestions)
              ? details.suggestions // If it's already an array, use it directly
              : details.suggestions.split('\n').map(suggestion => suggestion.trim()).filter(Boolean) // If it's a string, split and format it
          };
          return acc;
        }, {})
      };

      setReport(formattedReport);
      setIsDetailExpanded(false);
    } catch (error) {
      console.error('Error submitting assignment', error);
      setError('Failed to submit assignment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerHeight = 15;
    const headerHeight = 35;
    const pageContentHeight = pageHeight - footerHeight - headerHeight;

    // ... (keeping the header and footer functions the same) ...
    const addHeader = () => {
        const logoWidth = 100;
        const margin = 20;
        const logoEndX = logoWidth + margin;
  
        doc.setFontSize(18);
        doc.text('Writing Evaluation Report', logoEndX, 20, { align: 'left' });
        doc.addImage('/images/WriteZenLogo.jpeg', 'JPEG', 10, 10, logoWidth, 20);
        doc.line(10, 30, pageWidth - 10, 30);
    };

    const addFooter = (pageNumber) => {
        doc.setFontSize(10);
        doc.line(10, pageHeight - footerHeight, pageWidth - 10, pageHeight - footerHeight);
        doc.text(`Page ${pageNumber}`, 10, pageHeight - 10);
        doc.text('This is an AI-generated evaluation and AI can make mistakes.', pageWidth - 10, pageHeight - 10, { align: 'right' });
    };

    const checkPageBreak = (currentY, requiredSpace) => {
        if (currentY + requiredSpace > pageHeight - footerHeight) {
            doc.addPage();
            addHeader();
            return headerHeight;
        }
        return currentY;
    };

    const addContent = () => {
        let currentY = headerHeight + 5;
        const lineHeight = 10;
        const compactLineHeight = 7;
        const summaryLineHeight = 5; // Reduced line height for summary
        const explanationLineHeight = 5; // Reduced line height for explanations
        const suggestionLineHeight = 5; // Reduced line height for suggestions
        const sectionSpacing = 15;
        const articleLineHeight = 6;
        const leftMargin = 10;
        const rightMargin = 10;
        const textWidth = pageWidth - leftMargin - rightMargin;

        // Your Article Section (First Section)
        doc.setFontSize(14);
        doc.text('Your Article', leftMargin, currentY);
        currentY += lineHeight * 1.5;

        // Article Text
        doc.setFontSize(10);
        const articleText = doc.splitTextToSize(assignmentText, textWidth);
        
        let isFirstLine = true;
        articleText.forEach((line) => {
            currentY = checkPageBreak(currentY, articleLineHeight);
            doc.setFontSize(10);
            
            if (currentY === headerHeight && !isFirstLine) {
                currentY += articleLineHeight;
            }
            
            doc.text(line, leftMargin, currentY);
            currentY += articleLineHeight;
            isFirstLine = false;
        });

        // Start new page for Evaluation Criteria, Summary, and Scores
        doc.addPage();
        addHeader();
        currentY = headerHeight + 5;

        // Evaluation Criteria Section
        doc.setFontSize(14);
        doc.text('Evaluation Criteria', leftMargin, currentY);
        currentY += lineHeight;

        // Create evaluation criteria table
        doc.autoTable({
            startY: currentY,
            head: [],
            body: [
                ['Evaluation Standard', grade],
                ['Genre', genres],
                ['Evaluation Parameters', evaluationCriteria.join(', ')]
            ],
            theme: 'grid',
            styles: { 
                cellPadding: 5,
                fontSize: 10
            },
            columnStyles: {
                0: { fontStyle: 'bold', width: 60, fillColor: [248, 248, 248] },
                1: { width: 130 }
            },
            headStyles: {
                fillColor: [248, 248, 248],
                textColor: [0, 0, 0],
                fontStyle: 'bold'
            },
            margin: { bottom: footerHeight }
        });

        currentY = doc.lastAutoTable.finalY + sectionSpacing;

        // Evaluation Summary Section
        doc.setFontSize(14);
        doc.text('Evaluation Summary', leftMargin, currentY);
        currentY += lineHeight;

        doc.setFontSize(10);
        const splitSummary = doc.splitTextToSize(report.overallSummary, textWidth);
        
        splitSummary.forEach((line) => {
            currentY = checkPageBreak(currentY, summaryLineHeight);
            doc.setFontSize(10);
            doc.text(line, leftMargin, currentY);
            currentY += summaryLineHeight;
        });

        currentY += sectionSpacing;

        // Scores Section
        doc.setFontSize(14);
        doc.text('Scores', leftMargin, currentY);
        currentY += lineHeight;

        // Create scores table
        doc.autoTable({
            startY: currentY,
            head: [['Criteria', 'Score out of 10']],
            body: Object.entries(report.scoreTable).map(([criteria, score]) => [criteria.replace(/_/g, ' '), score]),
            theme: 'grid',
            styles: {
                cellPadding: 5,
                fontSize: 10
            },
            headStyles: {
                fillColor: [230, 230, 230],
                textColor: [0, 0, 0],
                fontStyle: 'bold'
            },
            margin: { bottom: footerHeight }
        });

        // Detailed Evaluation Section (Start on new page)
        doc.addPage();
        addHeader();
        currentY = headerHeight + 5;

        doc.setFontSize(14);
        doc.text('Detailed Evaluation', leftMargin, currentY);
        currentY += lineHeight * 1.5;

        const detailedEntries = Object.entries(report.detailedAnalysis);
        let criteriaCounter = 0;
        
        detailedEntries.forEach(([category, details], index) => {
            // Start new page after every two criteria
            if (criteriaCounter === 2) {
                doc.addPage();
                addHeader();
                currentY = headerHeight + 5;
                criteriaCounter = 0;
            }

            doc.setFontSize(12);
            doc.text(category, leftMargin, currentY);
            currentY += lineHeight;

            doc.setFont(undefined, 'bold');
            doc.setFontSize(10);
            doc.text(`Score: ${details.score}/10`, leftMargin + 5, currentY);
            doc.setFont(undefined, 'normal');
            currentY += explanationLineHeight + 2; // Small extra space after score

            // Extract the bold part and the regular part
            const boldPart = 'Explanation:';
            const regularPart = details.explanation;

            // Split the regular part into lines
            const regularTextLines = doc.splitTextToSize(regularPart, textWidth - 15);

            // Add the bold part
            currentY = checkPageBreak(currentY, explanationLineHeight);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold'); // Set font to bold
            doc.text(boldPart, leftMargin + 5, currentY);

            // Measure the width of the bold part to position the regular text correctly
            const boldPartWidth = doc.getTextWidth(boldPart);

            // Add the first line of the regular text right after the bold part on the same line
            doc.setFont('helvetica', 'normal'); // Set font back to normal
            doc.text(regularTextLines[0], leftMargin + 5 + boldPartWidth, currentY);

            // Move to the next line for the remaining text
            currentY += explanationLineHeight;

            // Add the rest of the lines
            regularTextLines.slice(1).forEach((line) => {
                currentY = checkPageBreak(currentY, explanationLineHeight);
                doc.text(line, leftMargin + 5, currentY);
                currentY += explanationLineHeight;
            });

            
            doc.setFont(undefined, 'bold');
            currentY += explanationLineHeight; // Small space before suggestions
            doc.text('Suggestions:', leftMargin + 5, currentY);
            doc.setFont(undefined, 'normal');
            currentY += explanationLineHeight;

            details.suggestions.forEach((suggestion, idx) => {
                const bulletPoint = `${idx + 1}. `;
                const suggestionText = doc.splitTextToSize(suggestion, textWidth - 30);
                
                currentY = checkPageBreak(currentY, suggestionLineHeight);
                doc.setFontSize(10);
                doc.text(bulletPoint, leftMargin + 10, currentY);
                doc.text(suggestionText, leftMargin + 15, currentY);
                currentY += suggestionLineHeight * suggestionText.length;
            });

            currentY += sectionSpacing;
            criteriaCounter++;
        });
    };

    // Generate the document
    addHeader();
    addContent();

    // Add footers to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(i);
    }

    // Save the PDF
    doc.save('WriteZEN_Evaluation_Report.pdf');
};

  const renderScoreCell = (score) => {
    const getColorClass = (score) => {
      if (score >= 8) return 'bg-green-200 text-green-800';
      if (score >= 5) return 'bg-yellow-200 text-yellow-800';
      return 'bg-red-200 text-red-800';
    };

    return (
      <div className={`p-2 text-center font-bold rounded ${getColorClass(score)} border-b border-gray-300`}>
        {score}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-100">
      {/* Navigation Bar */}
      {/* <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="/images/WriteZenLogo.jpeg"
                alt="WriteZEN Logo"
                className="h-10 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a 
                href="https://iwritealong.com/contact-us/" 
                className="text-gray-700 hover:text-white hover:bg-blue-600 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 shadow-md"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </nav> */}
  
      {/* Main Content */}
      <div className="flex items-center justify-center p-1 pt-1">
        <div className="w-full max-w-7xl bg-white shadow-2xl rounded-2xl overflow-hidden">
          <div className="p-1 bg-gradient-to-r from-blue-500 to-purple-600">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <BookOpen className="mr-3" />
              Evaluate
            </h2>
          </div>
  
          <form onSubmit={handleSubmit} className="p-2 space-y-3">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                {error}
              </div>
            )}
  
            {/* Flexbox Container for Left and Right Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-2 max-w-max">
                <div>
                  <label htmlFor="grade" className="block text-lg font-bold text-gray-700 mb-2">
                    Who am I?
                  </label>
                  <select
                    id="grade"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    {gradeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
  
                <div>
                  <label htmlFor="genres" className="block text-lg font-bold text-gray-700 mb-2">
                    What am I evaluating?
                  </label>
                  <select
                    id="genres"
                    value={genres}
                    onChange={(e) => setGenres(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Genre</option>
                      {Object.keys(genresOptions).map((category) => (
                        <optgroup label={category} key={category} className="border-t border-gray-300 mt-2">
                          {genresOptions[category].map((genre) => (
                            <option key={genre} value={genre} className="py-2">
                              {genre}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                  </select>
                </div>
  
                <div>
                  <label className="block text-lg font-bold text-gray-700 mb-2">
                    What is my evaluation criteria?
                  </label>
                  <div className="space-y-2">
                    {Object.entries(evaluationOptions).map(([category, criteria]) => (
                      <div key={category} className="bg-gray-200 p-2 rounded-lg">
                        <h3 className="font-medium text-gray-900 mb-2">{category}</h3>
                        <div className="grid grid-cols-1 gap-2">
                          {criteria.map((criterion) => (
                            <div key={criterion} className="flex items-center">
                              <input
                                type="checkbox"
                                id={criterion}
                                checked={evaluationCriteria.includes(criterion)}
                                onChange={() => handleEvaluationCriteriaChange(criterion)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label htmlFor={criterion} className="ml-2 text-sm text-gray-700">
                                {criterion}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
  
              {/* Right Column */}
              <div className="ml-[-270px]">
                <label htmlFor="assignmentText" className="block text-lg font-bold text-gray-700 mb-2">
                  Write here:
                </label>
                <textarea
                  id="assignmentText"
                  value={assignmentText}
                  onChange={handleTextChange}
                  rows={24}
                  className="w-full px-4 py-2 border border-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex justify-between items-center">
                  {warningMessage && (
                    <div className="text-yellow-600 font-semibold flex items-center">
                      <span className="mr-2">⚠️</span>{warningMessage}
                    </div>
                  )}
                  <p className="text-sm text-gray-600">
                    Word Count: {assignmentText.split(/\s+/).filter((word) => word).length} / {maxWordCount}
                  </p>
                </div>
                

                {isLoading ? (
                  <button
                    type="submit"
                    className="w-full py-2 bg-blue-500 text-white rounded-lg cursor-not-allowed"
                    disabled
                  >
                    Evaluating... it may take upto 90 seconds to complete the evaluation
                  </button>
                ) : (
                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      className={`w-full py-2 text-white rounded-lg ${
                        isSubmitDisabled || wordCount < 50
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-700'
                      }`}
                      disabled={isSubmitDisabled || wordCount < 50}
                    >
                      Submit (5 credits will be charged) <Send className="inline ml-2" />
                    </button>

                    <button
                      type="button"
                      className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700"
                      onClick={() => {
                        setGrade('');
                        setGenres('');
                        setEvaluationCriteria([]);
                        setAssignmentText('');
                        setError(null);
                        setWarningMessage(null);
                        setReport(null);
                      }}
                    >
                      Clear
                    </button>
                  </div>

                )}

                <div className="border-t border-gray-700 mt-2"></div>

                {report && (
                    <div className="p-6 bg-gradient-to-r from-green-500 to-blue-600 text-white mt-3 ">
                      <h2 className="text-xl font-bold text-center text-black">Evaluation Report</h2>
                      <div className="space-y-2">
                        <div>
                          <strong>Overall Summary:</strong>
                          <p>{report.overallSummary}</p>
                        </div>

                        <div>
                          <strong>Scores:</strong>
                          <div className="grid grid-cols-3 gap-2">
                            {/* Render scores for each evaluation criterion */}
                            {report.detailedAnalysis && Object.entries(report.detailedAnalysis).map(([category, details]) => (
                              <div key={category} className="p-2 text-center font-bold rounded bg-gray-500 border-b border-gray-300">
                                <div  className="whitespace-normal break-words">{category}</div>
                                <div>{details.score} / 10</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* AI Generated Text Analysis */}
                        <div className="mt-4">
                          <strong>AI Generated Text Analysis:</strong>
                          <p>
                            <span className="font-bold">Estimated AI-generated content:</span> {report.aiGeneratedTextAnalysis.percentage}% 
                          </p>
                          <p>
                            <span className="font-bold">Explanation:</span> {report.aiGeneratedTextAnalysis.explanation}
                          </p>
                        </div>

                        {/* <div className="pt-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault(); // Prevent form submission
                              setIsDetailExpanded(!isDetailExpanded);
                            }}
                            className="flex items-center text-white"
                          >
                            {isDetailExpanded ? (
                              <ChevronUp className="mr-2" />
                            ) : (
                              <ChevronDown className="mr-2" />
                            )}
                            {isDetailExpanded ? 'Hide Detailed Report' : 'Show Detailed Report'}
                          </button>
                        </div> */}

                        {/* {isDetailExpanded && ( */
                          <div className="mb-6">
                          {/* Render detailed analysis for each category */}
                            {report.detailedAnalysis && Object.entries(report.detailedAnalysis).map(([category, details], index) => (
                              <div 
                                key={category} 
                                className={`pb-4 mb-4 border-b border-gray-300 ${index === 0 ? 'border-t' : ''}`}
                              >
                                <h4 className="font-semibold text-gray-700">{category}</h4>
                                <p><strong>Score:</strong> {details.score}/10</p>
                                <p><strong>Explanation:</strong> {details.explanation}</p>
                                <div className="mt-2">
                                  <strong>Suggestions:</strong>
                                  <ul className="list-disc pl-5">
                                    {Array.isArray(details.suggestions) 
                                      ? details.suggestions.map((suggestion, index) => (
                                          <li key={index}>{suggestion}</li>
                                        ))
                                      : details.suggestions && details.suggestions.split('\n').map((suggestion, index) => (
                                          <li key={index}>{suggestion}</li>
                                        ))
                                    }
                                  </ul>
                                </div>
                              </div>
                            ))}
                        </div>
                        
                        }
                        <div>
                          <button
                            onClick={(e) => {
                              e.preventDefault(); // Prevent form submission
                              downloadReport();
                            }}
                            className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700"
                          >
                            Download Report <ClipboardCheck className="inline ml-2" />
                          </button>
                        </div>
                      </div>
                    </div>
                 )}
              </div>
            </div>
          </form>
  
          
        </div>
      </div>
    </div>
  );
  
};

export default AssignmentForm;
